import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../../../../../environment/environment';
import { AuthService } from '../../../../../../services/auth.service';
import { PermissionLoggerService } from '../../../../../../services/permission-logger.service';
import { PermissionService } from '../../../../../../services/permission.service';
import { UserImpersonationService } from '../../../../../../services/user-impersonation.service';
import { AssetStore } from '../../../../../../stores/asset.store';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { UserStore } from '../../../../../../stores/user.store';
import { Asset } from '../../../../../../types/asset.interface';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { PermissionScope } from '../../../../../../types/permission-scope.enum';
import { RolePermissionType } from '../../../../../../types/role.interface';
import { CampaignActionsService } from './campaign-actions.service';
import { CampaignHistoryService } from './campaign-history.service';
import { CampaignMergeService } from './campaign-merge.service';

interface RequiredPermission {
  permissionId: number;
  type: 'read' | 'write';
}

const getRequiredPermission = (item: MenuItem): RequiredPermission | undefined => {
  const data = item.data as { requiresPermission?: RequiredPermission } | undefined;
  return data?.requiresPermission;
};

@Injectable({
  providedIn: 'root',
})
export class CampaignNavigationService {
  private readonly assetStore = inject(AssetStore);
  private readonly campaignStore = inject(CampaignStore);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly actions = inject(CampaignActionsService);
  private readonly historyService = inject(CampaignHistoryService);
  private readonly permissionService = inject(PermissionService);
  private readonly logger = inject(PermissionLoggerService);
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly userStore = inject(UserStore);
  private readonly translate = inject(TranslocoService);
  private readonly mergeService = inject(CampaignMergeService);

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _currentAssetId = signal<number | null>(null);
  private readonly _currentCampaignId = signal<number | null>(null);
  private readonly _sourceTab = signal<string>('general');

  readonly currentAssetId = this._currentAssetId.asReadonly();
  readonly currentCampaignId = this._currentCampaignId.asReadonly();
  readonly sourceTab = this._sourceTab.asReadonly();
  readonly currentAsset = computed<Asset | undefined>(() => {
    const assetId = this.currentAssetId();
    if (!assetId) return undefined;
    return this.assetStore.byId()(assetId);
  });

  readonly currentCampaign = computed(() => {
    const campaignId = this._currentCampaignId();
    if (!campaignId) return null;
    return this.campaignStore.byId()(campaignId);
  });

  readonly isOnCampaignPage = computed(() => this._currentCampaignId() !== null);

  readonly isOnAssetPage = computed(() => this._currentAssetId() !== null);

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    const assetId = this.currentAssetId();
    const campaignId = this.currentCampaignId();
    const isModelPage = this.router.url.includes('/models/');

    items.push({
      label: this.translate.translate('navigation.campaigns'),
      routerLink: ['/assets', assetId, 'monitoring', 'campaigns'],
    });

    if (campaignId) {
      if (isModelPage) {
        const model = this.campaignModelStore.getById(campaignId);
        items.push({
          label: this.translate.translate('navigation.models'),
          routerLink: ['/assets', assetId, 'monitoring', 'campaigns', 'models'],
        });
        const modelLabel = model?.name && model.name.length > 0 ? model.name : String(campaignId);
        items.push({ label: `${this.translate.translate('entities.campaign.models.singular')} "${modelLabel}"` });
      } else {
        const campaign = this.currentCampaign();
        const campaignLabel = campaign?.timestamp
          ? this.historyService.formatLogDate(campaign.timestamp)
          : `${this.translate.translate('entities.campaign.singular')} ${campaignId}`;
        items.push({ label: campaignLabel });
      }
    }

    return items;
  });

  readonly assetMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    const assetId = this.currentAssetId();
    if (assetId === null) return items;

    items.push({
      label: this.translate.translate('navigation.models'),
      materialIcon: 'content_copy',
      command: () => {
        this.navigateToCampaignModels(assetId);
      },
    });

    const ongoingForAsset = this.campaignStore
      .entities()
      .filter((c: Campaign) => c.assetId === assetId && c.status === CampaignStatus.ON_GOING);
    const hasAnyOngoing = ongoingForAsset.length >= 1;
    const hasEnoughOngoing = ongoingForAsset.length >= 2;

    items.push({
      label: this.translate.translate('entities.campaign.actions.importMeasures'),
      materialIcon: 'upload_file',
      visible: hasAnyOngoing,
      command: () => {
        void this.actions.importMeasures(assetId);
      },
    });

    items.push({
      label: this.translate.translate('navigation.mergeCampaigns'),
      materialIcon: 'merge',
      visible: hasEnoughOngoing,
      command: () => {
        this.mergeService.openMergeDialog(assetId);
      },
    });

    return items;
  });

  readonly newCampaignMenuItems = computed(() => [
    {
      label: this.translate.translate('navigation.manualCampaign'),
      icon: 'pi pi-plus',
      command: () => {
        void this.logNavigationAction('create_manual_campaign');
      },
    },
    {
      label: this.translate.translate('navigation.fromTemplate'),
      icon: 'pi pi-copy',
      command: () => {
        void this.logNavigationAction('create_campaign_from_template');
      },
    },
  ]);

  constructor() {
    this.initRouteListener();
    this.updateIdsFromCurrentRoute();
  }

  private initRouteListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateIdsFromCurrentRoute();
      });
  }

  private updateIdsFromCurrentRoute(): void {
    const url = this.router.url;
    const assetId = this.extractAssetIdFromUrl(url);
    const campaignId = this.extractCampaignIdFromUrl(url);

    this._currentAssetId.set(assetId);
    this._currentCampaignId.set(campaignId);

    const tabMatch = url.match(/\/campaigns\/\d+\/(general|measures|auscultation|history)/);
    if (tabMatch?.[1]) {
      this._sourceTab.set(tabMatch[1]);
    }
  }

  extractAssetIdFromUrl(url: string): number | null {
    const assetMatch = url.match(/\/assets\/(\d+)/);
    return assetMatch?.[1] ? parseInt(assetMatch[1], 10) : null;
  }

  extractCampaignIdFromUrl(url: string): number | null {
    const campaignMatch = url.match(/\/(?:campaigns|models)\/(\d+)/);
    return campaignMatch?.[1] ? parseInt(campaignMatch[1], 10) : null;
  }

  navigateToCampaignModels(assetId: number): void {
    void this.logNavigationAction('entities.campaign.messages.navigateToModels', { assetId });
    void this.router.navigate(['/assets', assetId, 'monitoring', 'campaigns', 'models']);
  }

  navigateToCampaignPlanning(campaign: Campaign): void {
    void this.router.navigate(['/assets', campaign.assetId, 'monitoring', 'campaigns', campaign.id, 'planning']);
  }

  getCurrentCampaignUrl(): string | null {
    const assetId = this._currentAssetId();
    const campaignId = this._currentCampaignId();
    if (assetId && campaignId) {
      return `/assets/${assetId}/monitoring/campaigns/${campaignId}`;
    }
    return null;
  }

  async getFilteredCampaignMenuItems(campaign: Campaign): Promise<MenuItem[]> {
    const baseItems = this.buildBaseCampaignMenuItems(campaign);
    const requiredPermissions = this.extractRequiredPermissions(baseItems);

    if (requiredPermissions.length === 0) {
      return baseItems;
    }

    if (environment.name === 'dev' && !this.impersonationService.isImpersonating()) {
      return baseItems;
    }

    const realUserId = this.authService.currentUserId();
    if (realUserId) {
      const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);
      if (effectiveUserId) {
        const user = this.userStore.byId()(effectiveUserId);
        if (user?.isAdmin) {
          this.logger.log('ADMIN_BYPASS', {
            userId: effectiveUserId,
            assetId: campaign.assetId,
            permissionType: RolePermissionType.Write,
          });
          return baseItems;
        }
      }
    }

    const assetId = campaign.assetId || this._currentAssetId();
    if (!assetId) {
      return [];
    }

    const permissionResults = await this.checkPermissionsBatch(assetId, requiredPermissions);
    return this.filterMenuItemsByPermissions(baseItems, permissionResults);
  }

  getCampaignMenuItems(campaign: Campaign): MenuItem[] {
    return this.buildBaseCampaignMenuItems(campaign);
  }

  private buildBaseCampaignMenuItems(campaign: Campaign): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.translate.translate('entities.campaign.actions.planning'),
        icon: 'pi pi-calendar',
        visible: false,
        command: () => this.navigateToCampaignPlanning(campaign),
        data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
      },
      {
        label: this.translate.translate('entities.campaign.actions.exportPda'),
        icon: 'pi pi-file-export',
        visible: campaign.status !== CampaignStatus.DONE,
        command: () => {
          void this.actions.exportPDA(campaign);
        },
        data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
      },
      {
        label: this.translate.translate('entities.campaign.actions.resolveConflicts'),
        icon: 'pi pi-exclamation-triangle',
        visible: campaign.hasPendingConflicts === true,
        command: () => {
          void this.actions.resolveImportConflicts(campaign);
        },
        data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
      },
      {
        label: this.translate.translate('entities.campaign.actions.setAsTemplate'),
        icon: 'pi pi-save',
        command: () => {
          void this.actions.saveAsModel(campaign);
        },
        data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
      },
    ];

    if (campaign.status === CampaignStatus.DONE) {
      items.push({
        label: this.translate.translate('entities.campaign.actions.reopenCampaign'),
        icon: 'pi pi-refresh',
        command: () => {
          void this.actions.updateStatusWithHistory(campaign, CampaignStatus.ON_GOING);
        },
        data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
      });
    }

    if (campaign.status === CampaignStatus.ON_GOING) {
      items.push({
        label: this.translate.translate('entities.campaign.actions.validateN1'),
        icon: 'pi pi-check',
        command: () => {
          void this.actions.updateStatusWithHistory(campaign, CampaignStatus.N1_VALIDATED);
        },
        data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
      });
    }

    if (campaign.status === CampaignStatus.N1_VALIDATED) {
      items.push(
        {
          label: this.translate.translate('entities.campaign.actions.backToInProgress'),
          icon: 'pi pi-refresh',
          command: () => {
            void this.actions.updateStatusWithHistory(campaign, CampaignStatus.ON_GOING);
          },
          data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
        },
        {
          label: this.translate.translate('entities.campaign.actions.validateN2'),
          icon: 'pi pi-done',
          command: () => {
            void this.actions.updateStatusWithHistory(campaign, CampaignStatus.DONE);
          },
          data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
        },
      );
    }

    items.push({
      label: this.translate.translate('entities.campaign.messages.deleteCampaign'),
      icon: 'pi pi-trash',
      styleClass: 'p-menuitem-danger',
      visible: campaign.status !== CampaignStatus.DONE,
      command: () => {
        void this.actions.deleteWithHistory(campaign);
      },
      data: { requiresPermission: { permissionId: PermissionScope.Campaigns, type: 'write' } },
    });

    return items;
  }

  private extractRequiredPermissions(items: MenuItem[]): RequiredPermission[] {
    const permissions = items.flatMap(item => {
      const perm = getRequiredPermission(item);
      return perm ? [perm] : [];
    });
    const uniquePermissions = new Map<string, RequiredPermission>();
    for (const perm of permissions) {
      const key = `${perm.permissionId}-${perm.type}`;
      if (!uniquePermissions.has(key)) {
        uniquePermissions.set(key, perm);
      }
    }
    return Array.from(uniquePermissions.values());
  }

  private async checkPermissionsBatch(
    assetId: number,
    permissions: Array<{ permissionId: number; type: 'read' | 'write' }>,
  ): Promise<Map<string, boolean>> {
    const realUserId = this.authService.currentUserId();
    const userId = this.impersonationService.getEffectiveUserId(realUserId) ?? 0;
    const results = new Map<string, boolean>();

    if (!assetId || assetId === 0) {
      for (const perm of permissions) {
        results.set(`${perm.permissionId}-${perm.type}`, false);
      }
      return results;
    }

    const userPermissions = await this.permissionService.getUserPermissionsForAsset(userId, assetId);

    for (const perm of permissions) {
      const key = `${perm.permissionId}-${perm.type}`;
      const requiredType = perm.type === 'write' ? RolePermissionType.Write : RolePermissionType.ReadOnly;
      const userType = userPermissions.get(perm.permissionId);
      const hasAccess = userType !== undefined && userType !== RolePermissionType.None && userType >= requiredType;
      results.set(key, hasAccess);
    }

    return results;
  }

  private filterMenuItemsByPermissions(items: MenuItem[], permissionResults: Map<string, boolean>): MenuItem[] {
    return items.filter(item => {
      const requirement = getRequiredPermission(item);
      if (!requirement) return true;
      return permissionResults.get(`${requirement.permissionId}-${requirement.type}`) ?? false;
    });
  }

  private async logNavigationAction(key: string, params?: Record<string, unknown>): Promise<void> {
    try {
      const campaignId = this._currentCampaignId();
      if (campaignId) {
        const comment = params ? this.translate.translate(key, params) : key;
        await this.historyService.logStore.createLogWithDetails(
          {
            entity: 'campaign',
            entityId: campaignId,
            comment,
            userId: this.historyService.currentUserId(),
          },
          [],
        );
      }
    } catch (error) {
      console.warn(this.translate.translate('entities.campaign.messages.errorLoggingNavigation'), error);
    }
  }
}
